import events from '@/events';
import { EVENTS } from '@/events/hardware';
import { hexStringToUint8Array, wait } from '@/modules/utils';

const SERVICE_UUID = '74673364-0100-00ff-0000-68616e647300';
const ARMS_RANGE_UUID = '74673364-0100-00ff-0000-68616e647301';
const REALTIME_ARMS_UUID = '74673364-0100-00ff-0000-68616e647302';

const scannerIdInHex = str => {
  let hex = '01'; // PREFIX
  for (let i = 0; i < str.length; i += 1) {
    hex += `${str.charCodeAt(i).toString(16)}`;
  }
  return hex;
};

const WebBT = {
  isAvailable: false,
  enabled: false,
  keepSubscribing: false,
  list: {},
  device: null,

  async init() {
    this.startArmDetection = this.startArmDetection.bind(this);
    this.stopArmDetection = this.stopArmDetection.bind(this);
    if (!navigator.bluetooth) {
      console.log('[WebBT] not supported');
      this.onBTError();
      return;
    }
    try {
      if ('onavailabilitychanged' in navigator.bluetooth) {
        navigator.bluetooth.addEventListener('availabilitychanged', event =>
          this.changeState(event.value)
        );
      }
      const isAvailable = await navigator.bluetooth.getAvailability();
      this.changeState(isAvailable);
    } catch (error) {
      console.log('[WebBT] error: ', error);
      this.onBTError();
    }
  },

  changeState(isAvailable) {
    this.isAvailable = isAvailable;
    console.log('[WebBT] availability:', isAvailable);
    events.emit(WebBT.isAvailable ? EVENTS.BT_READY : EVENTS.BT_ERROR);
    if (this.isAvailable) {
      this.enable();
    }
  },

  enable() {
    if (!this.isAvailable || this.enabled) return;

    events.on(EVENTS.START_ARM_DETECTION, this.startArmDetection);
    events.on(EVENTS.STOP_ARM_DETECTION, this.stopArmDetection);
    this.enabled = true;
    console.log('[WebBT] enabled');
  },

  disable() {
    if (!this.isAvailable || !this.enabled) return;

    events.off(EVENTS.START_ARM_DETECTION, this.startArmDetection);
    events.off(EVENTS.STOP_ARM_DETECTION, this.stopArmDetection);
    this.enabled = false;
    console.log('[WebBT] disabled');
  },

  onBTReady: () => {
    events.emit(EVENTS.BT_READY);
  },

  onBTError: () => {
    events.emit(EVENTS.BT_ERROR);
  },

  onBTData: value => {
    const [...data] = new Uint8Array(value.buffer);
    if (data.length === 4) {
      console.log('[WebBT] arm ranges', data);
    }
    events.emit(EVENTS.BT_DATA, { data });
  },

  startArmDetection(scannerId, retryAttempts = 3) {
    console.log('[WebBT] start arm detection', scannerId, retryAttempts);
    if (!scannerId) return;

    this.connectToScanner(scannerId).catch(error => {
      console.log('[WebBT] connection error', error.message);
      if (retryAttempts > 1) {
        const retry = () => {
          this.startArmDetection(scannerId, retryAttempts - 1);
        };
        if (error && error.code === 19) {
          this.list[scannerId] = null;
          this.requestDevice(scannerId).then(retry);
        } else {
          wait(1).then(retry);
        }
      } else if (error) {
        console.log('[WebBT] failed to connect to device');
        events.emit(EVENTS.BT_DATA_ERROR, error);
      }
    });
  },

  stopArmDetection() {
    console.log('[WebBT] stop arm detection');
    this.disconnect();
  },

  getArmsRange(service) {
    return service
      .getCharacteristic(ARMS_RANGE_UUID)
      .then(characteristic => characteristic.readValue())
      .then(data => {
        console.log('[WebBT] arm ranges value received');
        this.onBTData(data);
      })
      .then(() => service);
  },
  subscribeForData(service) {
    return service
      .getCharacteristic(REALTIME_ARMS_UUID)
      .then(characteristic => characteristic.readValue())
      .then(data => {
        this.onBTData(data);
      })
      .then(() => {
        if (!this.keepSubscribing) return Promise.resolve();

        return this.subscribeForData(service);
      })
      .catch(error => {
        if (!this.keepSubscribing) return Promise.resolve();

        console.log('[ERROR]', '[WebBT] subscribeForData error: ', error);
        return Promise.reject(error);
      });
  },

  requestDevice(scannerId, request = false) {
    console.log('[WebBT] requesting device...', scannerId);
    if (!scannerId) return Promise.reject(new Error('no scanner id provided'));

    const manufacturerData = {
      companyIdentifier: 0xfe56,
      dataPrefix: hexStringToUint8Array(scannerIdInHex(scannerId)),
    };

    const options = {
      filters: [{ manufacturerData: [manufacturerData] }],
      optionalServices: [SERVICE_UUID],
    };

    if (!request && 'getDevices' in navigator.bluetooth) {
      return navigator.bluetooth.getDevices().then(devices => {
        if (devices.length === 0) return this.requestDevice(scannerId, true);

        console.log('[WebBT] permitted device found, connecting...');
        const device = devices[0];
        this.list[scannerId] = device;
        return device.gatt.connect();
      });
    }
    return navigator.bluetooth
      .requestDevice(options)
      .then(device => {
        this.list[scannerId] = device;
        console.log('[WebBT] request device done: ', scannerId);
        return device.gatt.connect();
      })
      .catch(error => {
        console.log(`[WebBT] request device error: ${error.message}`);
        return Promise.reject(error);
      });
  },

  connectToScanner(scannerId) {
    const device = this.list[scannerId];
    if (!scannerId || !device) {
      console.log('[WebBT] device not found');
      return Promise.reject();
    }

    this.device = device;
    return (device.gatt.connected ? Promise.resolve(device.gatt) : device.gatt.connect())
      .then(gatt => gatt.getPrimaryService(SERVICE_UUID))
      .then(service => this.getArmsRange(service))
      .then(service => {
        this.keepSubscribing = true;
        console.log('[WebBT] start subscribing bt data');
        return this.subscribeForData(service);
      });
  },

  disconnect() {
    this.keepSubscribing = false;
    if (!this.device || !this.device.gatt) return;

    this.device.gatt.disconnect();
    this.device = null;
    console.log('[WebBT] disconnected from device');
  },
};

export default WebBT;
